import type {
  AIProvenanceRecord,
  ProvenanceApplyResult,
  ProvenanceAsset,
  ProvenanceProvider,
} from '../types';
import { logProvenanceEvent } from './logging';

/**
 * No-op provider — used when C2PA signing is disabled or unavailable.
 * Marks machine-readable provenance as not applied without failing the job.
 */
export class NoOpProvenanceProvider implements ProvenanceProvider {
  readonly id = 'noop';

  async apply(
    asset: ProvenanceAsset,
    metadata: AIProvenanceRecord,
  ): Promise<ProvenanceApplyResult> {
    logProvenanceEvent({
      event: 'ai_provenance_skipped',
      app: metadata.app,
      assetId: metadata.outputAssetId,
      classification: metadata.classification,
      success: true,
      reason: 'NoOpProvenanceProvider — C2PA signing not enabled',
    });
    return {
      success: true,
      applied: false,
      reason: 'noop',
      output: asset.bytes ?? asset.url,
    };
  }
}

/**
 * C2PA / Content Credentials provider scaffold.
 *
 * Production signing requires:
 * - `@contentauth/c2pa-node` (Node >= 22) or an equivalent service
 * - Signing certificate + private key (never shipped to the browser)
 * - `C2PA_SIGNING_ENABLED=true`
 *
 * Until certificates are configured, this provider fails safely (does not
 * throw to the generation pipeline) and reports applied=false.
 */
export class C2PAProvenanceProvider implements ProvenanceProvider {
  readonly id = 'c2pa';

  constructor(
    private readonly options: {
      enabled: boolean;
      /** Optional injectable signer for tests / future edge wiring */
      signer?: (
        asset: ProvenanceAsset,
        metadata: AIProvenanceRecord,
      ) => Promise<ProvenanceApplyResult>;
    },
  ) {}

  async apply(
    asset: ProvenanceAsset,
    metadata: AIProvenanceRecord,
  ): Promise<ProvenanceApplyResult> {
    if (!this.options.enabled) {
      return new NoOpProvenanceProvider().apply(asset, metadata);
    }

    try {
      if (this.options.signer) {
        const result = await this.options.signer(asset, metadata);
        logProvenanceEvent({
          event: result.applied ? 'ai_provenance_applied' : 'ai_provenance_failed',
          app: metadata.app,
          assetId: metadata.outputAssetId,
          classification: metadata.classification,
          success: result.success && result.applied,
          reason: result.reason,
        });
        return result;
      }

      // Native C2PA signing is not wired in this deployment yet.
      // Intentionally do NOT mark provenance as applied.
      const reason =
        'C2PA signing enabled but no signer configured (cert/key or @contentauth/c2pa-node integration pending)';
      logProvenanceEvent({
        event: 'ai_provenance_failed',
        app: metadata.app,
        assetId: metadata.outputAssetId,
        classification: metadata.classification,
        success: false,
        reason,
      });
      return { success: true, applied: false, reason };
    } catch (err) {
      const reason = err instanceof Error ? err.message : 'C2PA apply failed';
      logProvenanceEvent({
        event: 'ai_provenance_failed',
        app: metadata.app,
        assetId: metadata.outputAssetId,
        classification: metadata.classification,
        success: false,
        reason,
      });
      // Fail safely — never block generation/download
      return { success: false, applied: false, reason };
    }
  }
}

export function createProvenanceProvider(opts: {
  c2paSigningEnabled: boolean;
  signer?: C2PAProvenanceProvider['options']['signer'];
}): ProvenanceProvider {
  if (opts.c2paSigningEnabled) {
    return new C2PAProvenanceProvider({
      enabled: true,
      signer: opts.signer,
    });
  }
  return new NoOpProvenanceProvider();
}

/**
 * Build a C2PA-oriented assertions payload (safe fields only).
 * Used by future signer implementations — never include prompts, keys, PII.
 */
export function buildSafeC2paAssertions(metadata: AIProvenanceRecord): Record<string, unknown> {
  const action =
    metadata.classification === 'AI_GENERATED'
      ? 'c2pa.created'
      : metadata.classification === 'AI_MODIFIED'
        ? 'c2pa.edited'
        : 'c2pa.edited';

  return {
    'c2pa.actions': {
      actions: [
        {
          action,
          softwareAgent: 'Rehub Studio',
          digitalSourceType:
            metadata.classification === 'AI_GENERATED'
              ? 'http://cv.iptc.org/newscodes/digitalsourcetype/trainedAlgorithmicMedia'
              : 'http://cv.iptc.org/newscodes/digitalsourcetype/compositeWithTrainedAlgorithmicMedia',
          parameters: {
            classification: metadata.classification,
            operation: metadata.operation,
            app: metadata.app,
            ...(metadata.provider ? { provider: metadata.provider } : {}),
            ...(metadata.model ? { model: metadata.model } : {}),
          },
        },
      ],
    },
    'c2pa.created': metadata.createdAt,
    'org.rehub.provenance': {
      version: metadata.provenanceVersion,
      classification: metadata.classification,
      operation: metadata.operation,
      sourceAssetId: metadata.sourceAssetId ?? null,
      outputAssetId: metadata.outputAssetId ?? null,
    },
  };
}
